import React, { useState, useEffect, useRef, useCallback } from 'react';

const AddServiceSuggestion = ({ 
  data, 
  handleServiceSuggestedClick, 
  inputRef,
  setServiceSuggested,
  focusedServiceIndex,
  setIsMouseOverSuggestion
}) => {
  const [selectedIndex, setSelectedIndex] = useState(-1);
  const [hoveredSuggestion, setHoveredSuggestion] = useState(null);
  const suggestionsRef = useRef(null);
  const itemRefs = useRef([]);

  const containerStyle = {
    position: "absolute",
    backgroundColor: "#fff",
    border: "1px solid #ccc",
    zIndex: 1000,
    maxHeight: "300px",
    overflowY: "auto",
    boxShadow: "0 2px 4px rgba(0, 0, 0, 0.1)",
    width: "345px",
    padding: "10px",
    borderRadius: "5px",
  };

  const categoryStyle = {
    fontWeight: "bold",
    padding: "8px 5px",
    backgroundColor: "#f8f8f8",
    borderBottom: "1px solid #ddd",
  };

  const itemStyle = {
    display: "flex",
    justifyContent: "space-between",
    padding: "8px",
    cursor: "pointer",
    borderBottom: "1px solid #eee",
  };

  // Create a flat array of all services for indexing (including category headers)
  const flatItems = [];
  const serviceToIndexMap = new Map();
  
  // Group services by category and create flat array
  const groupedServices = data.reduce((acc, service) => {
    const category = service.category || "Others";
    if (!acc[category]) {
      acc[category] = [];
    }
    acc[category].push(service);
    return acc;
  }, {});

  let currentIndex = 0;
  Object.entries(groupedServices).forEach(([category, services]) => {
    // Add category header to flat array
    flatItems.push({ type: "category", name: category, index: currentIndex });
    currentIndex++;
    
    // Add services to flat array
    services.forEach(service => {
      serviceToIndexMap.set(service._id, currentIndex);
      flatItems.push({ type: "service", ...service, index: currentIndex });
      currentIndex++;
    });
  });

  // Scroll selected item into view without smooth scrolling
  const scrollToItem = useCallback((index) => {
    if (index >= 0 && itemRefs.current[index] && suggestionsRef.current) {
      const item = itemRefs.current[index];
      const container = suggestionsRef.current;
      
      const itemTop = item.offsetTop;
      const itemBottom = itemTop + item.offsetHeight;
      const containerTop = container.scrollTop;
      const containerBottom = containerTop + container.offsetHeight;
      
      if (itemTop < containerTop) {
        container.scrollTop = itemTop;
      } else if (itemBottom > containerBottom) {
        container.scrollTop = itemBottom - container.offsetHeight;
      }
    }
  }, []);

  // Handle keyboard navigation
  useEffect(() => {
    const handleKeyDown = (e) => {
      if (!inputRef || !suggestionsRef.current) return;
      
      if (e.key === 'ArrowDown') {
        e.preventDefault();
        const maxIndex = flatItems.length - 1;
        let newIndex = selectedIndex >= maxIndex ? 0 : selectedIndex + 1;
        
        // Skip category headers when navigating
        while (newIndex < maxIndex && flatItems[newIndex]?.type === "category") {
          newIndex++;
        }
        
        setSelectedIndex(newIndex);
        scrollToItem(newIndex);
      } else if (e.key === 'ArrowUp') {
        e.preventDefault();
        const maxIndex = flatItems.length - 1;
        let newIndex = selectedIndex <= 0 ? maxIndex : selectedIndex - 1;
        
        // Skip category headers when navigating
        while (newIndex > 0 && flatItems[newIndex]?.type === "category") {
          newIndex--;
        }
        
        setSelectedIndex(newIndex);
        scrollToItem(newIndex);
      } else if (e.key === 'Enter' && selectedIndex >= 0 && flatItems[selectedIndex]?.type === "service") {
        e.preventDefault();
        const item = flatItems[selectedIndex];
        handleServiceSuggestedClick(item);
      } else if (e.key === 'Escape') {
        e.preventDefault();
        setServiceSuggested([]);
        setSelectedIndex(-1);
        if (inputRef) inputRef.blur();
      }
    };

    if (inputRef) {
      inputRef.addEventListener('keydown', handleKeyDown);
      return () => {
        if (inputRef) inputRef.removeEventListener('keydown', handleKeyDown);
      };
    }
  }, [flatItems, selectedIndex, inputRef, handleServiceSuggestedClick, setServiceSuggested, scrollToItem]);

  // Reset index when data changes
  useEffect(() => {
    setSelectedIndex(-1);
    itemRefs.current = itemRefs.current.slice(0, flatItems.length);
  }, [data]);

  const handleSuggestionClick = (service, e) => {
    e.preventDefault();
    e.stopPropagation();
    handleServiceSuggestedClick(service);
  };

  return (
    <div 
      style={containerStyle} 
      ref={suggestionsRef}
      onMouseEnter={() => setIsMouseOverSuggestion && setIsMouseOverSuggestion(true)}
      onMouseLeave={() => setIsMouseOverSuggestion && setIsMouseOverSuggestion(false)}
    >
      {Object.entries(groupedServices).map(([category, services]) => (
        <div key={category}>
          <div style={categoryStyle}>{category}</div>
          {services.map((service) => {
            const flatIndex = serviceToIndexMap.get(service._id);
            
            return (
              <div
                key={service._id}
                ref={el => itemRefs.current[flatIndex] = el}
                onClick={(e) => handleSuggestionClick(service, e)}
                style={{
                  ...itemStyle,
                  backgroundColor: (selectedIndex === flatIndex || hoveredSuggestion === service._id) ? "#6174DD" : "white",
                  color: (selectedIndex === flatIndex || hoveredSuggestion === service._id) ? "white" : "black",
                }}
                onMouseEnter={() => {
                  setHoveredSuggestion(service._id);
                  setSelectedIndex(flatIndex);
                }}
                onMouseLeave={() => setHoveredSuggestion(null)}
              >
                <span>{service.serviceName}</span>
                <span>₹{service.price}</span>
              </div>
            );
          })}
        </div>
      ))}
    </div>
  );
};

export default AddServiceSuggestion;